/* Product photographs render in Liquid before this progressively enhanced gallery runs. */
(() => {
  class MedicalProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.slides = [...this.querySelectorAll('.med-gallery-slide')];
      if (!this.slides.length) return;
      this.index = Math.max(0, this.slides.findIndex(slide => !slide.hidden));
      this.thumbnails = [...this.querySelectorAll('[data-med-thumb]')];
      this.thumbnails.forEach((thumb, index) => { thumb.dataset.medThumb = String(index); });
      this.dialog = this.querySelector('dialog');
      this.addEventListener('click', this.onClick, { signal });
      this.addEventListener('keydown', this.onKey, { signal });
      this.dialog?.addEventListener('close', this.onZoomClosed, { signal });
      // Matches Horizon's ThemeEvents.variantUpdate, scoped to this product section.
      this.closest('.shopify-section')?.addEventListener('variant:update', this.onVariantUpdate, { signal });
      this.querySelectorAll('[data-med-previous],[data-med-next]').forEach(button => { button.hidden = false; });
      this.dataset.enhanced = 'true';
      this.closest('.shopify-section')?.addEventListener('med:engraving-update', this.onEngravingUpdate, { signal });
      this.querySelector('[data-viewer=retry]')?.addEventListener('click', () => this.setModelActive(true), { signal });
      this.querySelector('.med-live-viewer')?.addEventListener('viewerchange', event => {
        this.modelPreviewSide = ['front', 'back'].includes(event.detail?.view) ? event.detail.view : null;
      }, { signal });
      this.closest('.shopify-section')?.addEventListener('change', this.onModelToneChange, { signal });
      this.select(this.index);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.modelViewer?.destroy();
      this.modelViewer = null;
      if (this.dialog?.open) this.dialog.close();
    }
    select(index, focusThumbnail = false) {
      const next = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        if (i !== next && !slide.hidden) {
          slide.querySelectorAll('video').forEach(video => video.pause());
          slide.querySelectorAll('iframe').forEach(frame => { frame.src = frame.src; });
          slide.querySelectorAll('model-viewer').forEach(model => model.pause?.());
        }
        slide.hidden = i !== next;
        if (slide.classList.contains('med-live-model')) this.setModelActive(i === next);
      });
      this.index = next;
      this.thumbnails.forEach((link, i) => {
        if (i === next) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      const status = this.querySelector('[data-med-gallery-status]');
      if (status) status.textContent = `${next + 1} / ${this.slides.length}`;
      const thumb = this.thumbnails[next];
      if (focusThumbnail) thumb?.focus({ preventScroll: true });
      const strip = this.querySelector('.med-gallery-thumbnails');
      if (strip && thumb && typeof strip.scrollTo === 'function') {
        const left = thumb.offsetLeft - strip.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
        strip.scrollTo({ left, behavior: 'instant' });
      }
    }
    engravingState(showBack = false) {
      const editor = [...(this.closest('.shopify-section')?.querySelectorAll('med-personalizer') || [])].find(item => item.dataset.productId === this.dataset.productId);
      const values = editor?.previewValues || {};
      const color = editor?.querySelector('[data-med-fill-color]:checked:not(:disabled)')?.dataset.medFillColor;
      const picker = [...(this.closest('.shopify-section')?.querySelectorAll('variant-picker') || [])].find(item => item.dataset.productId === this.dataset.productId);
      const selectedTone = [...(picker?.querySelectorAll('input[data-med-tone]:checked') || [])].find(input => !/secondary|symbol|logo/i.test(input.name))?.dataset.medTone;
      const allowed = (this.dataset.modelTones || 'silver').split(',');
      let tone = allowed.includes(selectedTone) ? selectedTone : allowed.includes(this.dataset.modelTone) ? this.dataset.modelTone : allowed[0];
      const selectedSymbol = [...(picker?.querySelectorAll('input[data-med-tone]:checked') || [])].map(input => input.dataset.medTone).find(value => ['red','blue'].includes(value));
      let symbol = selectedSymbol || this.dataset.modelSymbol || 'red';
      let band;
      // Bracelet colors describe both strap and plate. Use the reviewed product
      // option mapping, rather than interpreting "Black/Silver" as black metal.
      if (this.dataset.modelKind === 'bracelet' && this.modelConfig) {
        const variant = this.braceletVariant(picker);
        tone = variant?.tone || this.modelConfig.defaultTone || 'silver';
        band = variant?.band || this.modelConfig.defaultBand;
        symbol = variant?.symbol || this.modelConfig.defaultSymbol || 'red';
      }
      const blank = editor?.dataset.engravingMethod === 'none' || ['loading', 'error'].includes(editor?.dataset.fontState);
      const previewLines = (lines, side) => {
        if (editor?.sidesEnabled && !Object.hasOwn(values, side)) return [];
        if (blank || (this.modelConfig?.planes && !this.modelConfig.planes[side])) return [];
        if (lines.some(line => String(line).trim())) return [...lines];
        return [...(editor?.exampleLines || [])];
      };
      const front = previewLines(values.front || [], 'front');
      const back = previewLines(values.back || values.engraving || [], 'back');
      const lines = this.dataset.modelKind === 'cuff' ? (front.length ? front : back) : (back.length ? back : front);
      const previewSide = editor?.activePreviewSide === 'front' ? 'front' : 'back';
      return {lines, front, back, previewSide, tone, band, symbol, method: editor?.dataset.engravingMethod || 'standard', color: /^#[a-f0-9]{3,8}$/i.test(color || '') ? color : null, blank, showBack};
    }
    braceletVariant(picker) {
      const normalize = value => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
      const config = this.modelConfig;
      const optionIndex = config.options?.findIndex(option => /color|colour|finish|metal|style/i.test(option.name)) ?? -1;
      let selectedOptions = [];
      try { selectedOptions = JSON.parse(this.dataset.modelOptions || '[]') || []; } catch (_) { /* Older section markup. */ }
      let selected = selectedOptions[optionIndex];
      if (optionIndex >= 0 && picker) {
        const radio = [...picker.querySelectorAll('input[type=radio]:checked')].find(input => Number(input.dataset.fieldsetIndex) === optionIndex || input.name.startsWith(`${config.options[optionIndex].name}-`));
        const option = [...picker.querySelectorAll('select option:checked')].find(item => item.dataset.inputId?.startsWith(`${optionIndex + 1}-`));
        selected = radio?.value || option?.value || selected;
      }
      const title = normalize(this.dataset.modelVariantTitle);
      return config.variants?.find(variant => normalize(variant.label) === normalize(selected))
        || config.variants?.find(variant => normalize(variant.label) === title || title.startsWith(`${normalize(variant.label)} / `))
        || config.variants?.[0];
    }
    onModelToneChange = event => {
      const picker = event.target.closest('variant-picker');
      if (picker?.dataset.productId !== this.dataset.productId) return;
      queueMicrotask(() => this.modelViewer?.update(this.engravingState(false)));
    };
    onEngravingUpdate = event => {
      if (event.target.dataset.productId !== this.dataset.productId || !this.querySelector('.med-live-model')) return;
      const section = this.closest('.shopify-section');
      const typing = Boolean(event.detail?.typing);
      const firstTyping = typing && !section.dataset.medModelAutoShown;
      const state = this.engravingState(false);
      const onModel = this.slides[this.index]?.classList.contains('med-live-model');
      const sideChanged = Boolean(event.detail?.sideChanged);
      const showBack = firstTyping || sideChanged || (typing && onModel && state.previewSide !== this.modelPreviewSide);
      if (firstTyping || (sideChanged && !onModel)) {
        section.dataset.medModelAutoShown = 'true';
        this.modelShowBack = true;
        this.select(this.slides.findIndex(slide => slide.classList.contains('med-live-model')));
      }
      if (showBack) this.modelPreviewSide = state.previewSide;
      this.modelViewer?.update({...state, showBack});
    };
    async setModelActive(active) {
      this.modelActive = active;
      if (this.modelViewer) { this.modelViewer.setActive(active); return; }
      if (!active || this.modelLoading) return;
      const host = this.querySelector('.med-live-viewer');
      if (!host) return;
      this.modelLoading = true;
      const loading = host.querySelector('[data-viewer=loading]'), fallback = host.querySelector('[data-viewer=fallback]');
      loading.hidden = false; fallback.hidden = true;
      try {
        const [module, meshes, surfaces] = await Promise.all([import(host.dataset.viewerModule), fetch(host.dataset.viewerMesh).then(response => { if (!response.ok) throw new Error('Model unavailable'); return response.json(); }), host.dataset.viewerSurfaces ? import(host.dataset.viewerSurfaces) : null]);
        if (surfaces) surfaces.applyEngravingSurfaces(meshes);
        if (!this.isConnected) return;
        this.modelConfig = meshes;
        this.modelViewer = module.mount(host, meshes);
        const state = this.engravingState(Boolean(this.modelShowBack) || !this.modelPendingView);
        this.modelPreviewSide = state.previewSide;
        this.modelViewer.update(state);
        if (this.modelPendingView) this.modelViewer.view?.(this.modelPendingView);
        this.modelPendingView = null;
        this.modelShowBack = false;
        this.modelViewer.setActive(this.modelActive);
        loading.hidden = true;
      } catch (error) { host.dataset.viewerError = error.message || 'Preview unavailable'; loading.hidden = true; fallback.hidden = false; }
      finally { this.modelLoading = false; }
    }
    onClick = event => {
      const thumb = event.target.closest('[data-med-thumb]');
      if (thumb) { event.preventDefault(); this.select(Number(thumb.dataset.medThumb)); return; }
      if (event.target.closest('[data-med-previous]')) { this.select(this.index - 1); return; }
      if (event.target.closest('[data-med-next]')) { this.select(this.index + 1); return; }
      if (event.target.closest('[data-med-close-zoom]') || event.target === this.dialog) { this.dialog?.close(); return; }
      const zoom = event.target.closest('[data-med-zoom]');
      if (!zoom || !this.dialog || typeof this.dialog.showModal !== 'function') return;
      event.preventDefault();
      this.zoomTrigger = zoom;
      const photo = zoom.querySelector('img'), large = this.querySelector('[data-med-zoom-image]');
      large.src = zoom.href;
      large.alt = photo?.alt || 'Product photo';
      this.dialog.showModal();
      this.querySelector('[data-med-close-zoom]')?.focus();
    };
    onKey = event => {
      const thumb = event.target.closest('[data-med-thumb]');
      if (!thumb || this.dialog?.open) return;
      let next;
      if (event.key === 'ArrowRight') next = this.index + 1;
      if (event.key === 'ArrowLeft') next = this.index - 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.slides.length - 1;
      if (next === undefined) return;
      event.preventDefault(); this.select(next, true);
    };
    onZoomClosed = () => { this.zoomTrigger?.focus({ preventScroll: true }); };
    onVariantUpdate = event => {
      const productId = event.detail?.data?.newProduct?.id || event.detail?.data?.productId || event.target.dataset?.productId;
      if (productId && String(productId) !== this.dataset.productId) return;
      const fresh = event.detail?.data?.html?.querySelector?.('med-product-gallery');
      if (fresh) {
        const keepModel = this.slides[this.index]?.classList.contains('med-live-model');
        const sameProduct = fresh.dataset.productId === this.dataset.productId;
        const previousSide = this.modelViewer?.getStats?.().view || this.modelPreviewSide;
        if (keepModel && sameProduct) {
          fresh.modelShowBack = previousSide === 'front' || previousSide === 'back';
          fresh.modelPendingView = previousSide;
        }
        this.replaceWith(fresh);
        if (keepModel && sameProduct) fresh.select?.(fresh.slides.findIndex(slide => slide.classList.contains('med-live-model')));
        return;
      }
      const variant = event.detail?.resource;
      if (variant?.title) {
        const title = variant.title.toLowerCase();
        this.dataset.modelVariantTitle = variant.title;
        if (variant.options) this.dataset.modelOptions = JSON.stringify(variant.options);
        this.dataset.modelSymbol = title.includes('blue') ? 'blue' : 'red';
        this.dataset.modelTone = title.includes('black') ? 'black' : title.includes('rose') ? 'rose-gold' : title.includes('gold') ? 'gold' : 'silver';
        this.modelViewer?.update(this.engravingState(false));
      }
      const id = variant?.featured_media?.id || variant?.featured_image?.id;
      const index = this.slides.findIndex(slide => slide.dataset.medMediaId === String(id));
      if (index !== -1 && !this.slides[this.index]?.classList.contains('med-live-model')) this.select(index);
    };
  }
  if (!customElements.get('med-product-gallery')) customElements.define('med-product-gallery', MedicalProductGallery);
})();
